import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';

import type { LinkFilters } from './urlState';

interface LinkFilterChipsProps {
  linkFilters: LinkFilters;
  onRemove: (key: string) => void;
}

const ID_NOUNS: Record<string, { one: string; many: string }> = {
  policy_id: { one: 'Policy', many: 'Policies' },
  policy_id_in: { one: 'Policy', many: 'Policies' },
  verification_id: { one: 'VOB', many: 'VOBs' },
  verification_id_in: { one: 'VOB', many: 'VOBs' },
  member_id: { one: 'Member', many: 'Members' },
  member_id_in: { one: 'Member', many: 'Members' }
};

const itemsOf = (key: string, values: string[]): string[] =>
  key.endsWith('_in')
    ? values.flatMap((value) => value.split(',').map((item) => item.trim())).filter(Boolean)
    : values;

const humanize = (key: string): string => {
  const words = key.replace(/_in$/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const chipText = (key: string, values: string[]): { label: string; noun: string } => {
  const items = itemsOf(key, values);
  const idNoun = ID_NOUNS[key];

  if (idNoun) {
    return items.length === 1
      ? { label: `${idNoun.one}: ${items[0].slice(0, 8)}`, noun: idNoun.one.toLowerCase() }
      : { label: `${idNoun.many}: ${items.length}`, noun: idNoun.many.toLowerCase() };
  }

  const noun = humanize(key);
  return {
    label: `${noun}: ${items.length === 1 ? items[0] : `${items.length} values`}`,
    noun: noun.toLowerCase()
  };
};

const LinkFilterChips = ({ linkFilters, onRemove }: LinkFilterChipsProps): JSX.Element => (
  <>
    {Object.entries(linkFilters).map(([key, values]) => {
      const { label, noun } = chipText(key, values);

      return (
        <div
          key={key}
          className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
          <span
            className="text-sm text-blue-800 font-medium"
            title={itemsOf(key, values).join(', ')}>
            {label}
          </span>
          <button
            onClick={() => onRemove(key)}
            className="text-blue-600 hover:text-blue-800"
            aria-label={`Remove ${noun} filter`}
            title={`Remove ${noun} filter`}>
            <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
          </button>
        </div>
      );
    })}
  </>
);

export default LinkFilterChips;
